/**
 * MSAL bootstrap.
 *
 * Critical invariants:
 *   - MUST call `msalInstance.initialize()` and `handleRedirectPromise()`
 *     before rendering children. MSAL v3+ will silently stall otherwise and
 *     the app renders nothing → permanent white screen.
 *   - `tokenProvider()` handles its own redirect chain: cached silent token
 *     → ordinary prompt-less sign-in redirect. Omitting `prompt` lets Entra
 *     reuse a live first-party session without a separate forced-silent cycle.
 *     It returns `null` only when a redirect is already in flight — callers
 *     in api.ts surface that as `AuthRequiredError` (filtered by
 *     `isAuthPending`) so pages don't flash an "API 401" banner while the
 *     browser is navigating away. No in-app modal is shown — failed auth
 *     always goes through a full-page Entra redirect, Outlook-style.
 *   - Token acquisition is SINGLE-FLIGHTED and every redirect is latched by
 *     `redirectInFlight`. A page load requests tokens from many components at
 *     once; letting each run its own chain produced ~19 Entra token requests
 *     in 5s and tripped Entra's AADSTS50196 loop detector, locking users out.
 *     Never reintroduce a per-caller auth chain (this is why `ssoSilent` and
 *     the guards' private copies of the chain were removed).
 *   - Every code path returns visible UI — no bare `null` returns from
 *     route guards while a redirect is in flight.
 */

import { useEffect, useState, createContext, useContext, useCallback, type ReactNode } from 'react';
import {
  PublicClientApplication,
  EventType,
  type AccountInfo
} from '@azure/msal-browser';
import {
  MsalProvider,
  useMsal,
  useIsAuthenticated,
  AuthenticatedTemplate,
  UnauthenticatedTemplate
} from '@azure/msal-react';
import { msalConfig, interactiveLoginRequest, API_SCOPE, SILENT_REDIRECT_URI } from './auth-config';
import { setTokenProvider, api } from './api';

export const msalInstance = new PublicClientApplication(msalConfig);

/**
 * True when this document is MSAL's hidden silent-renew iframe carrying an auth
 * response.
 *
 * Silent renewal points that iframe at `SILENT_REDIRECT_URI` (a script-free
 * page), so normally the app never loads here. This is a backstop for the
 * failure that caused INC-003: if the frame ever lands on the SPA again, a
 * nested copy of the app would request its own token, spawn another iframe, and
 * recurse until Entra's loop detector locked the account out.
 *
 * When true we make no auth calls and leave `location.hash` untouched so the
 * parent window can still read the response it is waiting for.
 */
function detectAuthIframe(): boolean {
  try {
    if (typeof window === 'undefined' || window.top === window.self) return false;
    const hash = window.location.hash;
    return /[#&]state=/.test(hash) && /[#&](code|error|id_token|access_token)=/.test(hash);
  } catch {
    // Cross-origin frame access throws; treat as "not our auth iframe".
    return false;
  }
}

const IN_AUTH_IFRAME = detectAuthIframe();

// Short-window guard for interactive loginRedirects. An interactive redirect
// that lands the user right back on the SPA without a working session (e.g.
// handleRedirectPromise failed to exchange the auth code because the PKCE
// verifier was lost) would otherwise cascade into Entra's AADSTS50196
// "loop detected" error within ~3 hops.
// If we attempt 2+ interactive redirects within 60s and STILL aren't signed
// in, we stop the loop and show a recoverable error UI instead of bouncing
// the user to Entra again.
const INTERACTIVE_LOGIN_TS = 'mh-interactive-login-attempts';
const INTERACTIVE_LOOP_WINDOW_MS = 60 * 1000;
const INTERACTIVE_LOOP_MAX = 2;

interface InteractiveLoginAttempts {
  count: number;
  firstTs: number;
}

function readInteractiveLoginAttempts(): InteractiveLoginAttempts {
  if (typeof window === 'undefined') return { count: 0, firstTs: 0 };
  try {
    const raw = window.localStorage.getItem(INTERACTIVE_LOGIN_TS);
    if (!raw) return { count: 0, firstTs: 0 };
    const parsed = JSON.parse(raw) as Partial<InteractiveLoginAttempts>;
    return {
      count: Number(parsed.count) || 0,
      firstTs: Number(parsed.firstTs) || 0
    };
  } catch {
    return { count: 0, firstTs: 0 };
  }
}

function writeInteractiveLoginAttempts(v: InteractiveLoginAttempts): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(INTERACTIVE_LOGIN_TS, JSON.stringify(v));
  } catch {
    /* ignore */
  }
}

function clearInteractiveLoginAttempts(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(INTERACTIVE_LOGIN_TS);
  } catch {
    /* ignore */
  }
}

/**
 * Returns true if we've already fired too many interactive loginRedirects in
 * a short window and the SPA is back without a session. Callers MUST stop
 * redirecting in this case so the user isn't trapped in a redirect loop.
 */
function isInteractiveLoginLooping(): boolean {
  const a = readInteractiveLoginAttempts();
  if (a.count < INTERACTIVE_LOOP_MAX) return false;
  return Date.now() - a.firstTs < INTERACTIVE_LOOP_WINDOW_MS;
}

/**
 * Records an interactive loginRedirect attempt. If within the loop window,
 * increments the counter; otherwise resets to a fresh window starting now.
 */
function recordInteractiveLoginAttempt(): void {
  const now = Date.now();
  const a = readInteractiveLoginAttempts();
  if (a.firstTs && now - a.firstTs < INTERACTIVE_LOOP_WINDOW_MS) {
    writeInteractiveLoginAttempts({ count: a.count + 1, firstTs: a.firstTs });
  } else {
    writeInteractiveLoginAttempts({ count: 1, firstTs: now });
  }
}

// Module-scoped "loop detected" sentinel. Needed because `handleRedirectPromise`
// runs synchronously at module load, BEFORE React mounts and attaches its
// `'mhevents:auth-loop-detected'` event listener. Without this flag, an event
// dispatched during init is lost and the recoverable error UI never renders.
// `MsalAuthProvider` seeds its `loopDetected` state from this value on first
// render so we catch the early-dispatch case.
let pendingAuthLoopDetected = false;

/**
 * True when an error (or the current URL) carries Entra's AADSTS50196
 * "loop detected" signal. Once Entra emits this it has LATCHED its loop
 * counter for this client, so retrying the redirect is guaranteed to fail —
 * the only way out is to reset local auth state and let the user start over.
 */
function isLoopDetectedError(err: unknown): boolean {
  const parts: string[] = [];
  if (typeof err === 'string') parts.push(err);
  else if (err && typeof err === 'object') {
    const e = err as { errorCode?: unknown; errorMessage?: unknown; message?: unknown };
    for (const v of [e.errorCode, e.errorMessage, e.message]) {
      if (typeof v === 'string') parts.push(v);
    }
  }
  if (typeof window !== 'undefined') {
    parts.push(window.location.hash, window.location.search);
  }
  return parts.some((p) => p.includes('AADSTS50196'));
}

/**
 * Purge every scrap of local sign-in state so the next attempt starts clean.
 * Used when Entra's loop detector has latched — stale PKCE/interaction keys
 * are usually what keeps re-triggering the failing flow.
 */
async function purgeAuthStateForRecovery(): Promise<void> {
  try {
    await msalInstance.clearCache();
  } catch {
    /* ignore */
  }
  clearInteractiveLoginAttempts();
  if (typeof window === 'undefined') return;
  // MSAL keeps interaction-status keys in sessionStorage regardless of the
  // configured cacheLocation; leftovers there re-trigger the broken flow.
  try {
    for (let i = window.sessionStorage.length - 1; i >= 0; i--) {
      const k = window.sessionStorage.key(i);
      if (k && k.startsWith('msal.')) window.sessionStorage.removeItem(k);
    }
  } catch {
    /* ignore */
  }
  // Drop the failed auth response from the URL so a reload doesn't replay it.
  try {
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  } catch {
    /* ignore */
  }
}

function signalAuthLoopDetected(reason: string): void {
  pendingAuthLoopDetected = true;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('mhevents:auth-loop-detected', { detail: { reason } }));
  }
}

/**
 * Global latch: true once ANY sign-in redirect has been kicked off in this
 * page lifetime. The browser is about to navigate away, so every other caller
 * must stop trying to authenticate.
 *
 * Load-bearing: `tokenProvider`, `RequireAuth` and `RequireAdmin` can all
 * decide to redirect within the same tick. Without this latch they each fire
 * their own `authorize` request, which is exactly the burst that trips Entra's
 * AADSTS50196 loop detector.
 */
let redirectInFlight = false;

/** True when a sign-in redirect has already been started on this page. */
export function isRedirectInFlight(): boolean {
  return redirectInFlight;
}

/**
 * Single helper for all non-user-initiated interactive sign-in redirects.
 * Returns true if the redirect was kicked off, false if we're in a loop and
 * the caller should surface an error UI instead.
 *
 * Use this anywhere we'd otherwise call `instance.loginRedirect(...)` from
 * an automatic recovery path (route guards, tokenProvider fallback,
 * handleRedirectPromise catch). User-initiated `SignInButton` clicks
 * intentionally bypass the cooldown so a manual retry always works.
 */
export function tryInteractiveLoginRedirect(reason: string): boolean {
  // Never navigate MSAL's hidden iframe to a sign-in page.
  if (IN_AUTH_IFRAME) return false;
  // A redirect is already navigating the browser away — never start a second.
  if (redirectInFlight) return true;
  if (isInteractiveLoginLooping()) {
    // eslint-disable-next-line no-console
    console.error(
      `[auth] Interactive loginRedirect loop detected (${reason}). ` +
        'Suppressing redirect; user will see a recoverable error UI.'
    );
    signalAuthLoopDetected(reason);
    return false;
  }
  redirectInFlight = true;
  recordInteractiveLoginAttempt();
  msalInstance.loginRedirect(interactiveLoginRequest).catch((err) => {
    redirectInFlight = false;
    // eslint-disable-next-line no-console
    console.error(`[auth] prompt-less loginRedirect failed (${reason})`, err);
  });
  return true;
}

/**
 * Sign-in triggered by an explicit user click (the "Sign in" button).
 *
 * Deliberately IGNORES the interactive cooldown — a human clicking the button
 * must always be able to retry, and clearing the counters lets the automatic
 * paths work again afterwards. But it still respects `redirectInFlight`:
 * a double-click must NOT fire two concurrent `loginRedirect` calls, because
 * two simultaneous `authorize` requests is exactly the pattern that trips
 * Entra's loop detector (`AADSTS50196`) and latches the user out.
 */
export function startUserInitiatedLogin(): void {
  if (redirectInFlight) return;
  redirectInFlight = true;
  clearInteractiveLoginAttempts();
  msalInstance.loginRedirect(interactiveLoginRequest).catch((err) => {
    redirectInFlight = false;
    // eslint-disable-next-line no-console
    console.error('[auth] user-initiated loginRedirect failed', err);
  });
}

// Plug bearer-token acquisition into the API client.
//
// ── SINGLE-FLIGHT (load-bearing — do NOT remove) ────────────────────────────
// On page load MANY callers request a token at the same time: MeProvider
// (/api/me), FavoritesProvider (/api/favorites), TasteSettingsProvider
// (/api/me/taste/settings), HomePage (/api/events/published), the image-SAS
// call, and any admin page. Previously each caller independently ran the full
// silent chain, and every `ssoSilent` opened its own hidden iframe → its own
// `authorize` request. Entra's sign-in log recorded **19 token requests in ~5
// seconds** and the browser fired **3 concurrent `prompt=none` authorize calls
// within 24 ms**. That trips Entra's loop detector, which then LATCHES:
// every subsequent authorize/reprocess returns `AADSTS50196 — "We couldn't
// sign you in"`, locking the user out of the site entirely.
//
// So: all concurrent callers share ONE in-flight acquisition promise, and a
// failed acquisition is negatively cached for a few seconds. A normal page
// load must produce ~1 Entra token request, never a burst.
//
// ── CHAIN ──────────────────────────────────────────────────────────────────
//   1. `acquireTokenSilent` — cached access token, or a refresh-token
//      redemption. Entra caps SPA refresh tokens at 24h, so this covers the
//      common "came back later today" case with zero navigation.
//   2. Ordinary `loginRedirect` with no `prompt`. Entra's default behavior
//      reuses the sole live browser session, shows an account picker for
//      multiple sessions, or asks for credentials when no session remains.
//
// `ssoSilent` (hidden iframe) was deliberately REMOVED from this chain: it is
// blocked by third-party-cookie policy in Chrome/Edge so it nearly always
// failed, and each attempt still cost a full `authorize` request. A separate
// top-level `prompt=none` redirect was also removed after a production HAR,
// started after the initial loop error, showed it re-entering the still-latched
// AADSTS50196 flow before the following ordinary redirect succeeded. Step 2
// preserves first-party SSO without that extra auth cycle.
//
// Returns null only when a redirect is already in flight; api.ts converts
// that into an `AuthRequiredError` which admin pages filter via
// `isAuthPending()` (no red "API 401" banner while the browser is navigating).

/** Shared in-flight token acquisition. Concurrent callers await this promise. */
let inFlightTokenRequest: Promise<string | null> | null = null;

// Negative cache: when acquisition fails (or kicks off a redirect) we return
// null for a short window instead of letting the next caller immediately
// re-run the chain. Without this, a page with N failing API calls would still
// walk the chain N times, just sequentially instead of in parallel.
const FAILED_TOKEN_BACKOFF_MS = 5000;
let lastTokenFailureTs = 0;

async function acquireApiToken(): Promise<string | null> {
  const account = msalInstance.getActiveAccount();
  if (!account) {
    // No cached account — go straight to interactive sign-in.
    tryInteractiveLoginRedirect('tokenProvider:no-account');
    return null;
  }
  try {
    const r = await msalInstance.acquireTokenSilent({
      account,
      scopes: [API_SCOPE],
      // Park the hidden renew iframe on a script-free page. Without this the
      // frame loads the SPA, which requests its own token and recurses.
      redirectUri: SILENT_REDIRECT_URI
    });
    clearInteractiveLoginAttempts();
    return r.accessToken;
  } catch {
    // Silent cache/refresh-token path exhausted. Use one ordinary first-party
    // redirect. Leaving `prompt` unset preserves SSO when an Entra browser
    // session exists without first sending a separate prompt=none request.
    tryInteractiveLoginRedirect('tokenProvider:silent-exhausted');
    return null;
  }
}

setTokenProvider(async () => {
  // Hidden silent-renew iframe: making a request here is what recurses.
  if (IN_AUTH_IFRAME) return null;

  // An acquisition is already running — join it instead of starting a second
  // one. This is what collapses a 5-way parallel burst into a single request.
  if (inFlightTokenRequest) return inFlightTokenRequest;

  // Recently failed — don't re-hammer Entra. A redirect is usually already in
  // flight at this point and the browser is about to navigate away anyway.
  if (lastTokenFailureTs && Date.now() - lastTokenFailureTs < FAILED_TOKEN_BACKOFF_MS) {
    return null;
  }

  inFlightTokenRequest = (async () => {
    try {
      const token = await acquireApiToken();
      lastTokenFailureTs = token ? 0 : Date.now();
      return token;
    } catch {
      lastTokenFailureTs = Date.now();
      return null;
    } finally {
      // Release on the next microtask so callers that are still synchronously
      // chaining off this promise reuse it rather than starting a fresh one.
      const done = inFlightTokenRequest;
      void Promise.resolve().then(() => {
        if (inFlightTokenRequest === done) inFlightTokenRequest = null;
      });
    }
  })();

  return inFlightTokenRequest;
});

// `initPromise` is awaited by `<MsalAuthProvider>` before mounting children
// so we never render against an uninitialized MSAL instance.
const initPromise: Promise<void> = (async () => {
  await msalInstance.initialize();
  // Restore active account on hard refresh; pick first account if none active.
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
    msalInstance.setActiveAccount(accounts[0]);
  }
  msalInstance.addEventCallback((evt) => {
    if (evt.eventType === EventType.LOGIN_SUCCESS && evt.payload) {
      const acc = (evt.payload as { account?: AccountInfo }).account;
      if (acc) {
        msalInstance.setActiveAccount(acc);
        clearInteractiveLoginAttempts();
      }
    }
  });
  // Process any redirect-back from AAD. Failure here must not crash the app.
  try {
    // In MSAL's silent iframe the response belongs to the parent window, which
    // is polling this frame's URL for it. Consuming it here would both strip
    // the hash the parent needs and start a competing acquisition.
    if (IN_AUTH_IFRAME) return;
    const result = await msalInstance.handleRedirectPromise();
    if (result?.account) {
      msalInstance.setActiveAccount(result.account);
      // Successful round-trip → clear all loop guards so future attempts
      // are allowed again.
      clearInteractiveLoginAttempts();
    } else if (
      typeof window !== 'undefined' &&
      // AAD always includes `state=` on real auth responses. Requiring both
      // `state=` AND one of the response markers avoids stripping legitimate
      // deep-link fragments that happen to use words like `error` or `code`.
      /[#&]state=/.test(window.location.hash) &&
      /[#&](code|error|id_token|access_token)=/.test(window.location.hash) &&
      msalInstance.getAllAccounts().length === 0
    ) {
      // URL came back with an auth response in the fragment, but MSAL did
      // not produce a result AND no account is cached. This means the code
      // exchange silently failed (most often: PKCE code_verifier missing
      // from storage because the original loginRedirect happened in a
      // different tab / private mode, or storage was cleared between
      // redirect-out and redirect-back). Without intervention RequireAuth
      // will fire loginRedirect again and Entra will trip AADSTS50196.
      // Surface an error UI instead and let the user retry cleanly.
      // eslint-disable-next-line no-console
      console.error(
        '[auth] Auth response present in URL fragment but no account materialized. ' +
          'PKCE state likely lost. Forcing recoverable error UI.'
      );
      // Clean the URL so a refresh doesn't replay the bad #code= fragment.
      try {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      } catch { /* ignore */ }
      signalAuthLoopDetected('handleRedirectPromise:no-account-after-fragment');
    }
  } catch (err) {
    // Do NOT auto-fire another loginRedirect here. Instead let RequireAuth
    // handle recovery through `tryInteractiveLoginRedirect`, whose loop guard
    // surfaces an error UI after repeated failed round-trips.
    //
    // Special case: if Entra explicitly reports AADSTS50196 ("loop detected"),
    // its loop counter has LATCHED — every further authorize/reprocess will
    // fail the same way until the browser state is reset. Redirecting again is
    // guaranteed to fail and keeps the user locked out, so purge the local MSAL
    // state and render the recovery UI immediately.
    if (isLoopDetectedError(err)) {
      // eslint-disable-next-line no-console
      console.error('[auth] Entra reported AADSTS50196 (loop detected). Clearing MSAL cache.', err);
      await purgeAuthStateForRecovery();
      signalAuthLoopDetected('handleRedirectPromise:AADSTS50196');
    } else {
      // eslint-disable-next-line no-console
      console.warn('[auth] handleRedirectPromise failed; deferring to route guards.', err);
    }
  }
})();

function LoadingShell({ message = 'Loading…' }: { message?: string }) {
  return (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
      <div className="text-center">
        <div className="spinner-border text-primary" role="status" aria-hidden="true" />
        <div className="mt-3 text-muted">{message}</div>
      </div>
    </div>
  );
}

export function MsalAuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [initError, setInitError] = useState<Error | null>(null);
  const [loopDetected, setLoopDetected] = useState<boolean>(() => pendingAuthLoopDetected);
  const [clearingCache, setClearingCache] = useState(false);
  useEffect(() => {
    let cancelled = false;
    initPromise
      .then(() => { if (!cancelled) setReady(true); })
      .catch((err) => { if (!cancelled) setInitError(err instanceof Error ? err : new Error(String(err))); });
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    function onLoop() {
      setLoopDetected(true);
    }
    window.addEventListener('mhevents:auth-loop-detected', onLoop as EventListener);
    return () => window.removeEventListener('mhevents:auth-loop-detected', onLoop as EventListener);
  }, []);
  if (initError) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">
          <h2 className="h5">Sign-in initialization failed</h2>
          <p className="mb-2">{initError.message}</p>
          <button type="button" className="btn btn-sm btn-outline-dark" onClick={() => window.location.reload()}>Reload</button>
        </div>
      </div>
    );
  }
  if (loopDetected) {
    return (
      <div className="container py-5" role="alert" aria-live="assertive">
        <div className="alert alert-warning">
          <h2 className="h5">Sign-in is stuck in a loop</h2>
          <p className="mb-2">
            We tried to sign you in but Microsoft kept redirecting you back here without completing the sign-in.
            This usually means the browser cleared the page&apos;s storage between redirects (private/incognito mode,
            strict tracking protection, multiple tabs, or a recent browser update).
          </p>
          <p className="mb-3">Pick one:</p>
          <div className="d-flex flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={clearingCache}
              onClick={async () => {
                if (clearingCache) return;
                setClearingCache(true);
                try {
                  await msalInstance.clearCache();
                } catch { /* ignore */ }
                clearInteractiveLoginAttempts();
                // Also clear MSAL's interaction-status keys which live in
                // sessionStorage regardless of the configured cache location.
                try {
                  for (let i = window.sessionStorage.length - 1; i >= 0; i--) {
                    const k = window.sessionStorage.key(i);
                    if (k && k.startsWith('msal.')) window.sessionStorage.removeItem(k);
                  }
                } catch { /* ignore */ }
                window.location.href = '/';
              }}
            >
              {clearingCache ? 'Clearing…' : 'Clear sign-in cache and retry'}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-dark"
              disabled={clearingCache}
              onClick={() => {
                clearInteractiveLoginAttempts();
                window.location.reload();
              }}
            >
              Try again
            </button>
          </div>
          <p className="small text-muted mt-3 mb-0">
            If this keeps happening, try a regular (non-private) browser window, disable
            &quot;Block third-party cookies&quot; for this site, or open the site in only one tab.
          </p>
        </div>
      </div>
    );
  }
  if (!ready) return <LoadingShell message="Starting up…" />;
  return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
}

/**
 * Server-authoritative identity + role, sourced from `GET /api/me`. This is the
 * single source of truth for `isAdmin` on the client — it honors both the Entra
 * `Admin` app-role claim AND the backend break-glass allowlist
 * (`ADMIN_OIDS`/`ADMIN_EMAILS`), which raw ID-token parsing cannot see.
 */
export interface MeInfo {
  oid: string;
  email: string;
  name: string;
  isAdmin: boolean;
  roles: string[];
}

interface MeContextValue {
  me: MeInfo | null;
  /** True while the first /api/me fetch is in flight (no cached value yet). */
  loading: boolean;
  refresh: () => void;
}

const ME_CACHE_KEY = 'mh-me';

function readCachedMe(): MeInfo | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(ME_CACHE_KEY);
    return raw ? (JSON.parse(raw) as MeInfo) : null;
  } catch {
    return null;
  }
}

const MeContext = createContext<MeContextValue>({ me: null, loading: true, refresh: () => {} });

/**
 * Fetches `/api/me` once the user is authenticated and exposes it via context.
 * Seeds from a localStorage cache so `isAdmin`-gated UI renders instantly on
 * repeat visits (then revalidates in the background — SWR style). Must be
 * mounted INSIDE MsalAuthProvider (needs `useIsAuthenticated`).
 */
export function MeProvider({ children }: { children: ReactNode }) {
  const isAuthed = useIsAuthenticated();
  const [me, setMe] = useState<MeInfo | null>(() => readCachedMe());
  const [loading, setLoading] = useState<boolean>(() => readCachedMe() === null);

  const load = useCallback(async () => {
    try {
      const r = await api.get<MeInfo>('/api/me');
      setMe(r);
      try {
        window.localStorage.setItem(ME_CACHE_KEY, JSON.stringify(r));
      } catch {
        /* ignore quota errors */
      }
    } catch {
      // Leave any stale cached value in place; a transient /api/me failure
      // shouldn't downgrade a known admin to non-admin mid-session.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthed) {
      setMe(null);
      setLoading(false);
      try {
        window.localStorage.removeItem(ME_CACHE_KEY);
      } catch {
        /* ignore */
      }
      return;
    }
    void load();
  }, [isAuthed, load]);

  return <MeContext.Provider value={{ me, loading, refresh: () => void load() }}>{children}</MeContext.Provider>;
}

/** Full identity/role context (with loading state). */
export function useMe(): MeContextValue {
  return useContext(MeContext);
}

export function useIsAdmin(): boolean {
  return useContext(MeContext).me?.isAdmin === true;
}

export function SignInButton({ className = 'btn btn-outline-light btn-sm' }: { className?: string }) {
  return (
    <button
      type="button"
      className={className}
      onClick={() => startUserInitiatedLogin()}
    >
      Sign in
    </button>
  );
}

export function SignOutButton({ className = 'btn btn-outline-light btn-sm' }: { className?: string }) {
  const { instance, accounts } = useMsal();
  const name = accounts[0]?.name ?? accounts[0]?.username ?? 'Sign out';
  return (
    <button
      type="button"
      className={className}
      onClick={() => instance.logoutRedirect().catch(console.error)}
      title={`Signed in as ${name}`}
    >
      Sign out
    </button>
  );
}

/**
 * Single entry point for route guards to (re)establish a session.
 *
 * Both `RequireAuth` and `RequireAdmin` used to run their own copy of the
 * silent-SSO chain — including a hidden-iframe `ssoSilent` — which raced the
 * single-flighted `tokenProvider` and produced extra concurrent `authorize`
 * requests. That burst is what trips Entra's AADSTS50196 loop detector.
 *
 * Now there is exactly one path: an ordinary first-party redirect with no
 * forced `prompt`. It is latched by `redirectInFlight` so at most ONE
 * navigation is ever started per page lifetime.
 */
function ensureSignedIn(reason: string): void {
  if (redirectInFlight) return;
  tryInteractiveLoginRedirect(reason);
}

/**
 * Gate that requires the user to be signed in (any account). Recovery is
 * delegated to `ensureSignedIn` so this guard can never start a competing
 * authentication attempt.
 * Always renders a visible loading/redirecting state — never bare `null`.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const isAuthed = useIsAuthenticated();
  const { inProgress } = useMsal();

  useEffect(() => {
    if (isAuthed || inProgress !== 'none') return;
    ensureSignedIn('RequireAuth');
  }, [isAuthed, inProgress]);

  if (inProgress !== 'none') return <LoadingShell message="Signing you in…" />;
  if (!isAuthed) {
    return (
      <>
        <UnauthenticatedTemplate>
          <LoadingShell message="Redirecting to sign-in…" />
        </UnauthenticatedTemplate>
        <AuthenticatedTemplate>{children}</AuthenticatedTemplate>
      </>
    );
  }
  return <>{children}</>;
}

export function RequireAdmin({ children }: { children: ReactNode }) {
  const isAuthed = useIsAuthenticated();
  const { me, loading: meLoading } = useMe();
  const isAdmin = me?.isAdmin === true;
  const { inProgress } = useMsal();

  useEffect(() => {
    if (isAuthed || inProgress !== 'none') return;
    ensureSignedIn('RequireAdmin');
  }, [isAuthed, inProgress]);

  if (inProgress !== 'none') return <LoadingShell message="Signing you in…" />;
  if (!isAuthed) {
    return (
      <>
        <UnauthenticatedTemplate>
          <LoadingShell message="Redirecting to sign-in…" />
        </UnauthenticatedTemplate>
        <AuthenticatedTemplate>{children}</AuthenticatedTemplate>
      </>
    );
  }
  // Authenticated but /api/me hasn't resolved yet — show a spinner rather than
  // flashing the "Admin role required" warning to an admin mid-load.
  if (meLoading && !me) return <LoadingShell message="Checking permissions…" />;
  if (!isAdmin) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning" role="alert">
          <h2 className="h5">Admin role required</h2>
          <p className="mb-0">
            You're signed in, but your account doesn't have the <code>Admin</code> role on this app.
            Ask the owner to assign it in the <em>mhevents-api</em> app registration → Users and groups.
          </p>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}
